import { Controller } from "@hotwired/stimulus"

const CHECK = '<svg class="mt-0.5 size-3.5 shrink-0 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>'
const WARN = '<svg class="mt-0.5 size-3.5 shrink-0 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4M12 17h.01"/></svg>'
const OPEN = '<span class="mt-0.5 block size-3.5 shrink-0 rounded-full border border-stone-300"></span>'

// The new-listing form: keeps the guide preview and the pre-publish checks up to date as you type,
// suggests venues the city's listings have used (filling the address, area and pin), and ⌘↵ publishes.
export default class extends Controller {
  static targets = [
    "title", "type", "artists", "day", "dayInput", "otherDay", "otherDayInput", "times", "starts", "ends", "until", "timeUnknown", "ongoing",
    "venue", "venues", "address", "area", "areaNote", "lat", "lng", "pinNote", "link", "rsvp", "publish",
    "previewWhen", "previewKind", "previewTitle", "previewMeta", "checkSource", "checkPin", "checkSimilar"
  ]
  static values = { venuesUrl: String, similarUrl: String, cityId: Number, weekId: Number, typeLabels: Object, typeColours: Object }

  connect() {
    this.suggestions = []
    this.refresh()
    this.checkSimilar()
  }

  disconnect() {
    clearTimeout(this.venueTimer)
    clearTimeout(this.similarTimer)
  }

  shortcut(event) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.element.requestSubmit(this.publishTarget)
    }
  }

  // ── When ──

  get selectedDay() {
    if (this.hasOtherDayInputTarget && this.otherDayInputTarget.value) return this.otherDayInputTarget.value
    if (this.hasDayInputTarget) return this.dayInputTarget.value
    return this.dayTargets.find((input) => input.checked)?.value || ""
  }

  pickDay() {
    if (this.hasOtherDayInputTarget) {
      this.otherDayInputTarget.value = ""
      this.otherDayTarget.classList.add("hidden")
    }
    this.refresh()
    this.checkSimilar()
  }

  anotherDate() {
    this.otherDayTarget.classList.remove("hidden")
    this.otherDayInputTarget.focus()
  }

  pickOtherDay() {
    if (this.otherDayInputTarget.value) this.dayTargets.forEach((input) => (input.checked = false))
    this.refresh()
    this.checkSimilar()
  }

  toggleTimes() {
    const ongoing = this.ongoingTarget.checked
    const unknown = this.timeUnknownTarget.checked
    this.timesTarget.classList.toggle("hidden", ongoing)
    this.untilTarget.classList.toggle("hidden", !ongoing)
    this.startsTarget.disabled = unknown
    this.endsTarget.disabled = unknown
    this.refresh()
  }

  // ── Where ──

  suggestVenues() {
    this.latTarget.value = ""
    this.lngTarget.value = ""
    this.areaNoteTarget.classList.add("hidden")
    clearTimeout(this.venueTimer)
    const query = this.venueTarget.value.trim()
    if (query.length < 2) return this.hideVenues()
    this.venueTimer = setTimeout(() => this.loadVenues(query), 150)
  }

  async loadVenues(query) {
    const response = await fetch(`${this.venuesUrlValue}&${new URLSearchParams({ q: query })}`, { headers: { Accept: "application/json" } })
    if (!response.ok || this.venueTarget.value.trim() !== query) return
    this.suggestions = (await response.json()).venues
    this.active = 0
    this.renderVenues()
  }

  renderVenues() {
    if (this.suggestions.length === 0) return this.hideVenues()
    this.venuesTarget.innerHTML = ""
    this.suggestions.forEach((venue, index) => {
      const option = document.createElement("button")
      option.type = "button"
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", String(index === this.active))
      option.className = "flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left aria-selected:bg-accent"
      option.innerHTML = '<svg class="mt-0.5 size-4 shrink-0 text-stone-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>'
      const text = document.createElement("span")
      text.className = "min-w-0"
      const name = document.createElement("span")
      name.className = "block truncate text-[13px] font-medium"
      name.textContent = venue.name
      const detail = document.createElement("span")
      detail.className = "block truncate text-[12px] text-muted-foreground"
      detail.textContent = [venue.address, venue.area, `used by ${venue.uses} listing${venue.uses === 1 ? "" : "s"}`].filter(Boolean).join(" · ")
      text.append(name, detail)
      option.append(text)
      option.addEventListener("mousedown", (event) => event.preventDefault())
      option.addEventListener("click", () => this.useVenue(venue))
      this.venuesTarget.append(option)
    })
    this.venuesTarget.classList.remove("hidden")
    this.venueTarget.setAttribute("aria-expanded", "true")
  }

  venueKeys(event) {
    if (this.venuesTarget.classList.contains("hidden")) return
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      this.active = (this.active + step + this.suggestions.length) % this.suggestions.length
      this.renderVenues()
    } else if (event.key === "Enter" && !(event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.useVenue(this.suggestions[this.active])
    } else if (event.key === "Escape") {
      event.stopPropagation()
      this.hideVenues()
    }
  }

  useVenue(venue) {
    this.venueTarget.value = venue.name
    if (venue.address) this.addressTarget.value = venue.address
    if (venue.area_id && [...this.areaTarget.options].some((option) => option.value === String(venue.area_id))) {
      this.areaTarget.value = String(venue.area_id)
      this.areaNoteTarget.classList.remove("hidden")
    }
    this.latTarget.value = venue.lat ?? ""
    this.lngTarget.value = venue.lng ?? ""
    this.hideVenues()
    this.refresh()
    this.checkSimilar()
  }

  addressEdited() {
    this.latTarget.value = ""
    this.lngTarget.value = ""
    this.refresh()
  }

  hideVenuesSoon() {
    setTimeout(() => this.hideVenues(), 120)
  }

  hideVenues() {
    this.venuesTarget.classList.add("hidden")
    this.venueTarget.setAttribute("aria-expanded", "false")
  }

  // ── Checks ──

  checkSimilar() {
    clearTimeout(this.similarTimer)
    this.similarTimer = setTimeout(() => this.loadSimilar(), 300)
  }

  async loadSimilar() {
    const params = { day: this.selectedDay, city_id: this.cityIdValue, title: this.titleTarget.value, venue: this.venueTarget.value }
    if (!params.day || (params.title.trim().length < 4 && !params.venue.trim())) return this.setCheck(this.checkSimilarTarget, "open", "No similar listing that day")

    const response = await fetch(`${this.similarUrlValue}?${new URLSearchParams(params)}`, { headers: { Accept: "application/json" } })
    if (!response.ok) return
    const { similar } = await response.json()
    if (similar.length === 0) return this.setCheck(this.checkSimilarTarget, "ok", "No similar listing that day")

    const first = similar[0]
    this.setCheck(this.checkSimilarTarget, "warn", `Similar that day: “${first.title}”${similar.length > 1 ? ` and ${similar.length - 1} more` : ""}`, first.path)
  }

  setCheck(item, state, text, path = null) {
    item.querySelector("[data-check-icon]").innerHTML = { ok: CHECK, warn: WARN, open: OPEN }[state]
    const label = item.querySelector("[data-check-text]")
    label.className = state === "open" ? "text-muted-foreground" : state === "warn" ? "text-amber-800" : ""
    label.textContent = text
    if (path) {
      const link = document.createElement("a")
      link.href = path
      link.target = "_blank"
      link.className = "ml-1 underline underline-offset-2"
      link.textContent = "Open"
      label.append(link)
    }
  }

  // ── Preview ──

  refresh() {
    const type = this.typeTargets.find((input) => input.checked)?.value || "other"
    const day = this.selectedDay ? new Date(`${this.selectedDay}T12:00:00`) : null
    const ongoing = this.ongoingTarget.checked
    const unknown = this.timeUnknownTarget.checked
    const starts = this.startsTarget.value
    const ends = this.endsTarget.value

    this.previewTitleTarget.textContent = this.titleTarget.value.trim() || "Untitled"
    const when = ongoing ? "Ongoing" : unknown ? "Time to be confirmed" : [starts, ends].filter(Boolean).map(this.clock).join("–")
    this.previewKindTarget.innerHTML = ""
    const dot = document.createElement("span")
    dot.className = "ui-dot"
    dot.style.background = this.typeColoursValue[type] || "#a8a29e"
    const kind = document.createElement("span")
    kind.className = "text-emerald-700"
    kind.textContent = [this.typeLabelsValue[type], when].filter(Boolean).join(" · ")
    this.previewKindTarget.append(dot, kind)

    const area = this.areaTarget.value ? this.areaTarget.selectedOptions[0].text : ""
    this.previewMetaTarget.textContent = [this.venueTarget.value.trim() || area, this.artistsTarget.value.split(",").map((name) => name.trim()).filter(Boolean).join(", ")]
      .filter(Boolean).join(" · ")
    this.previewWhenTarget.textContent = day ? [day.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" }), ongoing ? null : this.partOfDay(starts, unknown)].filter(Boolean).join(" · ") : ""

    const link = this.linkTarget.value.trim() || this.rsvpTarget.value.trim()
    this.setCheck(this.checkSourceTarget, link ? "ok" : "warn", link ? "Source link to check against" : "No event page to check it against")
    const pinned = this.latTarget.value && this.lngTarget.value
    this.setCheck(this.checkPinTarget, pinned ? "ok" : "open", pinned ? "Pin from a known venue" : "No pin yet: find pins from the edition after saving")
    this.pinNoteTarget.querySelector("span").textContent = pinned ? "Pin from a known venue." : "No pin yet: pick a known venue, or find pins from the edition after saving."
  }

  clock(time) {
    const [hours, minutes] = time.split(":").map(Number)
    const suffix = hours < 12 ? "am" : "pm"
    const hour = hours % 12 || 12
    return minutes ? `${hour}:${String(minutes).padStart(2, "0")}${suffix}` : `${hour}${suffix}`
  }

  partOfDay(time, unknown) {
    if (unknown || !time) return null
    const hour = Number(time.split(":")[0])
    return hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening"
  }
}
